import { KiziLogger } from '@kizi/comparator-core';
import React, { createContext, useState, useCallback } from 'react';

interface Premium {
  currentPrice: number;
  newPrice: number;
  hasChanged: boolean;
}

export interface CarPremiumContextType {
  loading: boolean;
  error?: string;
  requestLogId?: string;
  hasNewPremium: (startDate: string, selectedProduct: any) => Promise<boolean>;
  tempProduct: any | null;
  premium: Premium;
}

const initialPremium: Premium = {
  currentPrice: 0,
  newPrice: 0,
  hasChanged: false,
};

class ProductNotFoundError extends Error {
  constructor(productId: string) {
    super(`Product with ID ${productId} not found in comparison results`);
    this.name = 'ProductNotFoundError';
  }
}

class InvalidPriceError extends Error {
  constructor(currentPrice: any, newPrice: any) {
    super(
      `Invalid price information: current=${currentPrice}, new=${newPrice}`,
    );
    this.name = 'InvalidPriceError';
  }
}

export const CarPremiumContext = createContext<CarPremiumContextType>({
  loading: false,
  hasNewPremium: async () => false,
  tempProduct: null,
  premium: {
    currentPrice: 0,
    newPrice: 0,
    hasChanged: false,
  },
});

const API_BASE_URL = process.env.KIZI_PUBLIC_API_BASE_URL;
const API_AUTH_TOKEN = process.env.KIZI_PUBLIC_API_AUTH_TOKEN;

const getEncodedBody = (): URLSearchParams | null => {
  try {
    const storedBody = localStorage.getItem('encodedBody');
    if (!storedBody) return null;

    return new URLSearchParams(storedBody);
  } catch (e) {
    KiziLogger.logError({
      eventId: 0,
      member: 'car',
      message: 'Error parsing stored encoded body',
      eventName: 'parseError',
      data: { error: e },
    });
    return null;
  }
};

export const CarPremiumProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>();
  const [requestLogId, setRequestLogId] = useState<string>();
  const [tempProduct, setTempProduct] = useState<any | null>(null);
  const [premium, setPremium] = useState<Premium>(initialPremium);

  const hasNewPremium = useCallback(
    async (startDate: string, selectedProduct: any): Promise<boolean> => {
      if (
        selectedProduct?.provider?.id !== '120' &&
        selectedProduct?.provider?.id !== '94'
      ) {
        return false;
      }
      try {
        // Reset states at the beginning
        setTempProduct(null);
        setPremium(initialPremium);
        setError(undefined);
        setRequestLogId(undefined);
        const formData = getEncodedBody();
        if (!formData) {
          throw new Error('No encoded body found in storage');
        }

        formData.set('commencingdate', startDate);
        localStorage.setItem('encodedBody', formData.toString());
        setLoading(true);

        const response = await fetch(`${API_BASE_URL}/cars/comparisons/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            Authorization: `Basic ${API_AUTH_TOKEN}`,
          },
          body: formData,
        });

        const body = await response.json();

        if (response.status >= 400 || body.statusCode >= 400) {
          throw new Error(body.data || 'API request failed');
        }

        if (body.statusCode !== 200) {
          throw new Error('Invalid API response');
        }

        const productFromApi = body.data.find(
          (product) => product.id === selectedProduct.id,
        );

        if (!productFromApi) {
          KiziLogger.logError({
            eventId: 0,
            member: 'car',
            message: 'Product not found in API response',
            eventName: 'productNotFound',
            data: { selectedProductId: selectedProduct.id },
          });
          throw new ProductNotFoundError(selectedProduct.id);
        }

        // Safe access to nested properties with type guards
        const currentPrice = selectedProduct?.price?.month?.total;
        const newPrice = productFromApi?.price?.month?.total;

        if (typeof currentPrice !== 'number' || typeof newPrice !== 'number') {
          KiziLogger.logError({
            eventId: 0,
            member: 'car',
            message: 'Missing or invalid price information',
            eventName: 'invalidPrices',
            data: { currentPrice, newPrice },
          });
          throw new InvalidPriceError(currentPrice, newPrice);
        }

        // Update all states only after all validations pass
        setRequestLogId(body.meta.requestLogId);
        setTempProduct(productFromApi);
        setPremium({
          currentPrice,
          newPrice,
          hasChanged: currentPrice !== newPrice,
        });

        return currentPrice !== newPrice;
      } catch (error) {
        // Handle specific error types
        if (
          error instanceof ProductNotFoundError ||
          error instanceof InvalidPriceError
        ) {
          setError(error.message);
        } else {
          KiziLogger.logError({
            eventId: 0,
            member: 'car',
            message: 'Error fetching product',
            eventName: 'fetchError',
            data: { error },
          });
          setError(
            error instanceof Error ? error.message : 'Error fetching product',
          );
        }
        // Re-throw the error to be handled by the caller
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return (
    <CarPremiumContext.Provider
      value={{
        loading,
        error,
        requestLogId,
        hasNewPremium,
        tempProduct,
        premium,
      }}
    >
      {children}
    </CarPremiumContext.Provider>
  );
};
